// React
import React from 'react';
import PropTypes from 'prop-types';

import ReactModal from 'react-modal';
import Icon from 'src/react/components/icon/Icon';

import classNames from 'classnames';

import 'src/react/components/modal/modal.scss';

// We pulled ModalCloseButton into a separate component to show how the Modal contents can be
// composed using reasonable defaults and allowing for overrides. We likely want to pull this
// into it's own file and introduce any other reasonable defaults as needed.
const ModalCloseButton = ({ onClick }) => (
  <button
    id="Modal_close"
    data-testid="Modal_close"
    type="button"
    className="close"
    onClick={onClick}
  >
    <Icon name="close" />
    <span className="sr-only">Close</span>
  </button>
);

ModalCloseButton.propTypes = {
  onClick: PropTypes.func.isRequired
};

const DEFAULT_STYLE_OVERRIDE = {
  overlay: {
    backgroundColor: null
  },
  content: {
    top: null,
    left: null,
    right: null,
    bottom: null,
    border: null,
    background: null,
    borderRadius: null,
    padding: null,
    position: null,
    overflow: null
  }
};

const customStyles = {
  content: {
    top: '50%',
    transform: 'translate(0, -50%)'
  }
};

const modalPropTypes = {
  children: PropTypes.any,
  className: PropTypes.string,
  testId: PropTypes.string,
  onRequestClose: PropTypes.func,
  isOpen: PropTypes.bool,
  width: PropTypes.oneOf(['SM', 'MD', 'LG']),
  shouldCloseOnOverlayClick: PropTypes.bool,
  ModalCloseButtonComponent: PropTypes.element,
  isCentered: PropTypes.bool
};

const modalDefaultProps = {
  className: '',
  testId: '',
  isOpen: true,
  shouldCloseOnOverlayClick: true,
  parentSelector: null
};

export const getModalStyles = (isCentered) => {
  if (isCentered) {
    return {
      ...DEFAULT_STYLE_OVERRIDE,
      content: {
        ...DEFAULT_STYLE_OVERRIDE.content,
        ...customStyles.content
      }
    };
  }

  return DEFAULT_STYLE_OVERRIDE;
};

function Modal(props) {
  const {
    width,
    className,
    isOpen,
    onRequestClose,
    shouldCloseOnOverlayClick,
    testId,
    children,
    isCentered = false,
    ModalCloseButtonComponent = ModalCloseButton,
    ...rest
  } = props;

  const modalStyles = getModalStyles(isCentered);

  const overlayClassName = classNames('Modal__Bootstrap', className, {
    'Modal__Bootstrap--sm': width === 'SM',
    'Modal__Bootstrap--md': width === 'MD',
    'Modal__Bootstrap--lg': width === 'LG'
  });

  return (
    <ReactModal
      testId="Modal_content"
      style={modalStyles}
      className="modal-dialog"
      overlayClassName={overlayClassName}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Modal"
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      {...rest}
    >
      <div className="modal-content" data-testid={testId}>
        <ModalCloseButtonComponent onClick={onRequestClose} />
        <div className="modal-body">{children}</div>
      </div>
    </ReactModal>
  );
}

Modal.propTypes = modalPropTypes;
Modal.defaultPropTypes = modalDefaultProps;

export default Modal;
